import { FluidOption } from '@lmig/fluid-core';
import { JsonDocs, JsonDocsProp } from '@stencil/core/internal';

import { convertNonBooleanIndicatorToBoolean } from '../components/fluid-table/internal/fluid-table-select.utils';

export interface Example {
  name: string;
  element: string;
  container?: string;
  props: {}[];
  events: string[];
  sanitised?: any;
  children?: any[];
  content?: any;
  toggles?: any[];
  definition?: any;
}

export interface ExtendedJsonDocsComponent {
  props: any[];
  examples?: Example[];
  displayName?: string;
  changeLog?: any[];
  builder?: boolean;
  contextData?: string;
  excludeTag?: boolean;
  experimental?: boolean;
  analyticsAware?: string;
  testData?: any;
  childOf?: string;
  internal?: string;
}

const DASH_CASE_REGEXP = /-+([a-z0-9])/g;
const NON_ALPHANUMERIC_REGEXP = /[\W_]+/g;

// ================================================================ //
// -- Prop Type Checkers

export const isObjectProp = (props: object, propName: string) => {
  return typeof props[propName] === 'object';
};

export const isArrayProp = (props: object, propName: string) => {
  return Array.isArray(props[propName]);
};

export const getComplexProps = (props: object = {}) => {
  return Object.keys(props)
    .filter((prop) => isObjectProp(props, prop) || isArrayProp(props, prop))
    .filter((prop) => prop !== 'style')
    .reduce((complex: {}, current) => {
      complex[current] = props[current];
      return complex;
    }, {});
};

export const getSimpleProps = (props: object = {}) => {
  return Object.keys(props)
    .filter((prop) => !isObjectProp(props, prop) && !isArrayProp(props, prop))
    .reduce((complex: {}, current) => {
      complex[current] = props[current];
      return complex;
    }, {});
};

// ================================================================ //
// -- Convert to case

export const toInlineEvent = (eventName: string) => {
  return (
    typeof eventName === 'string' && `on${camelCaseToPascalCase(eventName)}`
  );
};

export const dashCaseToCamelCase = (input: string): string => {
  return input
    .replace(DASH_CASE_REGEXP, (...m: any[]) => m[1].toUpperCase())
    .replace(NON_ALPHANUMERIC_REGEXP, '');
};

export const camelCaseToPascalCase = (str: string): string => {
  return (
    str?.substring(0, 1)?.toUpperCase() +
    str?.substring(1, str?.length).replace(NON_ALPHANUMERIC_REGEXP, '')
  );
};

export const spaceCaseToCamelCase = (str: string): string => {
  return str
    ?.split(' ')
    .map((s: string, idx: number) =>
      idx === 0 ? s.toLowerCase() : camelCaseToPascalCase(s)
    )
    .join('')
    .replace(NON_ALPHANUMERIC_REGEXP, '');
};

export const spaceCaseToOptions = (
  options: string[]
): { label: string; value: string }[] => {
  return options?.map((option) => ({
    label: option,
    value: spaceCaseToCamelCase(option),
  }));
};

export const isEmptyObject = (ob: object) => Object.keys(ob).length === 0;

// ================================================================ //
// -- Get properties of type utilities

export const getToggleableOptions = (component: ExtendedJsonDocsComponent) =>
  (component?.props || []).filter((p) => p.type === 'boolean');

export const getStringOptions = (component: ExtendedJsonDocsComponent) =>
  (component?.props || []).filter((p) => p.type === 'string');

export const getNumberOptions = (component: ExtendedJsonDocsComponent) =>
  (component?.props || []).filter(
    (p) =>
      p.type === 'number' ||
      (isMultiType(p.type) && p.type.indexOf('number') !== -1)
  );

export const getArrayTypeOptions = (
  component: ExtendedJsonDocsComponent,
  docs: JsonDocs
) => {
  return (component?.props || [])
    .filter((p) => p.type.indexOf('[]') !== -1)
    .map((prop) => {
      const type = prop.type.replace('[]', '');
      return {
        type: type,
        name: prop.name,
        value: docs['interfaces'][type] || findEnum(type, docs) || type,
      };
    });
};

export const getEnumOptions = (
  component: ExtendedJsonDocsComponent,
  docs: JsonDocs
) => {
  return (component?.props || [])
    .filter((p) => hasEnum(p.type))
    .map((prop) => ({
      name: isRemappedAttribute(prop.attr, prop.name) ? prop.attr : prop.name,
      defaultValue: prop.default
        ? getValueFromEnumDeclaration(prop.default, docs)
        : undefined,
      docs: prop.docs,
      type: findEnum(prop.type, docs)?.name,
      options: findEnum(prop.type, docs)?.options,
    }));
};

export const getValueFromEnumDeclaration = (
  enumDeclaration: string,
  docs: JsonDocs
) => {
  const enumValue = enumDeclaration.split('.')[1];
  return findEnum(enumDeclaration, docs)?.options.find(
    (opt) => opt.label === enumValue
  )?.value;
};

export const getInterfaceOptions = (
  component: ExtendedJsonDocsComponent,
  docs: JsonDocs
) => {
  return component.props
    .filter((p) => !!docs['interfaces'][p.type])
    .map((prop) => ({
      name: prop.name,
      type: prop.type,
      options: Object.keys(docs['interfaces'][prop.type]).map((key) => ({
        label: key,
        value: docs['interfaces'][prop.type][key],
      })),
    }));
};

// ================================================================ //
// -- Property name formatting utilities

export const toKebabCase = (val: string): string => {
  return val.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

export const toFormLabel = (name: string): string => {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

// ================================================================ //
// -- Property type checking utilities

export const isRemappedAttribute = (attr: string, name: string): boolean => {
  return attr !== toKebabCase(name);
};

export const getConfiguratorDefault = (prop: JsonDocsProp) => {
  return prop.name === 'overrideGlobalTheme'
    ? true
    : convertNonBooleanIndicatorToBoolean(prop.default);
};

export const isMultiType = (type: string) => {
  return type.indexOf('|') !== -1 && type.indexOf('Fluid') === -1;
};

export const hasEnum = (type: string) => {
  return type.indexOf('|') !== -1 && type.indexOf('Fluid') !== -1;
};
export const isEnum = (type: string, docs: JsonDocs) => !!docs['enums'][type];
export const isType = (type: string, typeCheck: string) => type === typeCheck;
export const isInterface = (type: string, docs: JsonDocs) =>
  !!docs['interfaces'][type];
export const isArray = (type: string) => type.indexOf('[]') !== -1;
export const arrayType = (type: string) => type.replace('[]', '');

// ================================================================ //
// -- Object finder utilities

export const findEnum = (type: string, docs: JsonDocs) => {
  const enumName = type.substring(0, type.indexOf('.'));
  const enumStruct = docs['enums'][enumName];
  if (enumStruct) {
    const options = Object.keys(enumStruct).map((key) => ({
      label: key,
      value: enumStruct[key],
    }));
    return { name: enumName, options };
  }
  return undefined;
};

export const enumOptions = (type: string, docs: JsonDocs): FluidOption[] => {
  return hasEnum(type)
    ? findEnum(type, docs).options
    : Object.keys(docs['enums'][type]).map((key) => ({
        label: key,
        value: docs['enums'][type][key],
      }));
};
